import React, { useEffect, useState } from "react";
import DashboardComponent from "./dashboard.component";
import { useDispatch } from "react-redux";
import { Spin } from "antd";
import { useAppSelector } from "../../hooks/reduxHooks";
import {
  getOverviewMetricsApi,
  getReportingAverageProcessingTimeApi,
  getReportingChannelMsgCountApi,
  getReportingCtaCountApi,
  getReportingEvaluationCountApi,
  getReportingMessageCountApi,
  getReportingUniqueUsersPerDayApi,
  getReportingLanguageCountApi,
  getReportingCtaTypeCountApi,
  getTopicCountApi,
  getReportingChannelMsgCountByRawIdApi,
  getReportingProductCountApi,
} from "../../services/dashboard.service";
import { getTopicsApi } from "../../services/cta.service";
import { getCreditBalanceApi } from "../../services/credit.service";

const DashboardContainer = () => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [fetchCompleted, setFetchCompleted] = useState(false);
  const [overviewData, setOverviewData] = useState({
    unifiedReport: {},
    averageProcessingTime: 0,
    channelMsgCount: 0,
    channelMsgCountByRawId: 0,
    ctaCount: {
      data: [],
      total_chats: 0,
      average_human_Interventions: 0,
    },
    evaluationCount: 0,
    messageCount: [],
    uniqueUsersPerDay: [],
    languageCount: [],
    ctaTypeCount: [],
    ctaTopics: [],
    topicCount: {},
    creditBalance: {
      total_credits: 0,
      remaining: 0,
    },
    productCount: 0,
  });

  const state = useAppSelector((store) => store);
  const { dateRangeValue } = state?.dateRange;

  const callApi = (
    api,
    data,
    successCallback,
    failureCallback,
    finalCallback
  ) => {
    const startDate = new Date(data.start_date);
    const endDate = new Date(data.end_date);

    dispatch(
      api({
        data: {
          start_date: `${startDate.getFullYear()}-${startDate.getMonth() + 1}-${startDate.getDate()}`,
          end_date: `${endDate.getFullYear()}-${endDate.getMonth() + 1}-${endDate.getDate()}`,
        },
        finalCallback: () => {
          if (finalCallback) finalCallback();
        },
        successCallback: (response) => {
          if (successCallback) successCallback(response);
        },
        failureCallback: (error) => {
          console.log("Error in dashboard api call ->", error);
          if (failureCallback) failureCallback(error);
        },
      })
    );
  };

  useEffect(() => {
    if (!dateRangeValue?.start_date || !dateRangeValue?.end_date) {
      setFetchCompleted(true);
      return;
    }

    setLoading(true);
    setFetchCompleted(false);

    let pendingApis = 14;

    const onApiComplete = () => {
      pendingApis -= 1;
      if (pendingApis === 0) {
        setLoading(false);
        setFetchCompleted(true);
      }
    };

    const unifiedReport = () => {
      callApi(
        getOverviewMetricsApi,
        dateRangeValue,
        (response) => {
          setOverviewData((prevState) => ({
            ...prevState,
            unifiedReport: response,
          }));
        },
        null,
        onApiComplete
      );
    };

    const averageMessageProcessingTime = () => {
      callApi(
        getReportingAverageProcessingTimeApi,
        dateRangeValue,
        (response) => {
          setOverviewData((prevState) => ({
            ...prevState,
            averageProcessingTime: response,
          }));
        },
        null,
        onApiComplete
      );
    };

    const channelMsgCount = () => {
      callApi(
        getReportingChannelMsgCountApi,
        dateRangeValue,
        (response) => {
          setOverviewData((prevState) => ({
            ...prevState,
            channelMsgCount: response,
          }));
        },
        null,
        onApiComplete
      );
    };

    const channelMsgCountByRawId = () => {
      callApi(
        getReportingChannelMsgCountByRawIdApi,
        dateRangeValue,
        (response) => {
          setOverviewData((prevState) => ({
            ...prevState,
            channelMsgCountByRawId: response,
          }));
        },
        null,
        onApiComplete
      );
    };

    const ctaCount = () => {
      callApi(
        getReportingCtaCountApi,
        dateRangeValue,
        (response) => {
          setOverviewData((prevState) => ({
            ...prevState,
            ctaCount: response,
          }));
        },
        null,
        onApiComplete
      );
    };

    const ctaTypeCount = () => {
      callApi(
        getReportingCtaTypeCountApi,
        dateRangeValue,
        (response) => {
          setOverviewData((prevState) => ({
            ...prevState,
            ctaTypeCount: response,
          }));
        },
        null,
        onApiComplete
      );
    };

    const evaluationCount = () => {
      callApi(
        getReportingEvaluationCountApi,
        dateRangeValue,
        (response) => {
          setOverviewData((prevState) => ({
            ...prevState,
            evaluationCount: response,
          }));
        },
        null,
        onApiComplete
      );
    };

    const messageCount = () => {
      callApi(
        getReportingMessageCountApi,
        dateRangeValue,
        (response) => {
          setOverviewData((prevState) => ({
            ...prevState,
            messageCount: response,
          }));
        },
        null,
        onApiComplete
      );
    };

    const uniqueUsersPerDay = () => {
      callApi(
        getReportingUniqueUsersPerDayApi,
        dateRangeValue,
        (response) => {
          setOverviewData((prevState) => ({
            ...prevState,
            uniqueUsersPerDay: response,
          }));
        },
        null,
        onApiComplete
      );
    };

    const languageCount = () => {
      callApi(
        getReportingLanguageCountApi,
        dateRangeValue,
        (response) => {
          setOverviewData((prevState) => ({
            ...prevState,
            languageCount: response,
          }));
        },
        null,
        onApiComplete
      );
    };

    const ctaTopics = () => {
      dispatch(
        getTopicsApi({
          params: {},
          finalCallback: onApiComplete,
          successCallback: (response) => {
            const topics = Array.isArray(response) ? response : [];
            setOverviewData((prevState) => ({
              ...prevState,
              ctaTopics: topics.map((item) => ({
                topic: item.topic,
                count: 0,
              })),
            }));
          },
          failureCallback: (error) => {
            console.log("Error in getTopicsApi ->", error);
          },
        })
      );
    };

    const topicCount = () => {
      callApi(
        getTopicCountApi,
        dateRangeValue,
        (response) => {
          setOverviewData((prevState) => ({
            ...prevState,
            topicCount: response,
          }));
        },
        null,
        onApiComplete
      );
    };

    const productCount = () => {
      callApi(
        getReportingProductCountApi,
        dateRangeValue,
        (response) => {
          setOverviewData((prevState) => ({
            ...prevState,
            productCount: response,
          }));
        },
        null,
        onApiComplete
      );
    };

    const balance = () => {
      dispatch(
        getCreditBalanceApi({
          params: {},
          finalCallback: onApiComplete,
          successCallback: (response) => {
            setOverviewData((prevState) => ({
              ...prevState,
              creditBalance: response?.balance || {
                total_credits: 0,
                remaining: 0,
              },
            }));
          },
          failureCallback: (error) => {
            console.log("Error in getCreditBalanceApi ->", error);
          },
        })
      );
    };

    unifiedReport();
    averageMessageProcessingTime();
    channelMsgCount();
    channelMsgCountByRawId();
    ctaCount();
    ctaTypeCount();
    evaluationCount();
    messageCount();
    uniqueUsersPerDay();
    languageCount();
    ctaTopics();
    topicCount();
    productCount();
    balance();
  }, [dateRangeValue, dispatch]);

  return (
    <Spin spinning={loading}>
      <DashboardComponent data={overviewData} fetchCompleted={fetchCompleted} />
    </Spin>
  );
};

export default DashboardContainer;
